import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { AiSuggestionRegenScope } from '@/types/schema';

export function isAiSuggestionRegenScope(
  value: string | null | undefined
): value is AiSuggestionRegenScope {
  return Object.values(AiSuggestionRegenScope).includes(
    value as AiSuggestionRegenScope
  );
}

// Query param consumed by EstateTrusts to auto-open the testamentary entity
// modal for the referenced subtrust.
export const EDIT_TESTAMENTARY_ENTITY_ID_QUERY_PARAM =
  'editTestamentaryEntityId';

// Query param consumed by ManageDispositionsPage to auto-open the
// dispositive-provisions modal for the referenced entity or testamentary
// entity. The page derives the active section from the target's PULID kind.
export const OPEN_DISPOSITIONS_FOR_TARGET_ID_QUERY_PARAM =
  'openDispositionsForTargetId';

interface GetAISuggestionRegenRedirectPathArgs {
  scope: AiSuggestionRegenScope;
  householdId: string;
  // The regen target. Required for ENTITY (entity ID), SUBTRUST (testamentary
  // entity ID), and DISPOSITIONS (entity or testamentary entity ID); ignored
  // for HOUSEHOLD.
  targetId?: string;
}

// Resolves the in-app destination for an AI suggestion regen completion
// notification. Used by both the email redirect handler and the in-app
// notification click handler so the two flows stay in sync.
export function getAISuggestionRegenRedirectPath({
  scope,
  householdId,
  targetId,
}: GetAISuggestionRegenRedirectPathArgs): string {
  switch (scope) {
    case AiSuggestionRegenScope.Household:
      return getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS, {
        householdId,
      });
    case AiSuggestionRegenScope.Entity: {
      if (!targetId) {
        throw new Error('targetId is required for ENTITY scope');
      }
      return getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
        householdId,
        entityId: targetId,
      });
    }
    case AiSuggestionRegenScope.Subtrust: {
      if (!targetId) {
        throw new Error('targetId is required for SUBTRUST scope');
      }
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS,
        { householdId },
        { [EDIT_TESTAMENTARY_ENTITY_ID_QUERY_PARAM]: targetId }
      );
    }
    case AiSuggestionRegenScope.Dispositions: {
      if (!targetId) {
        throw new Error('targetId is required for DISPOSITIONS scope');
      }
      return getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
        { householdId },
        { [OPEN_DISPOSITIONS_FOR_TARGET_ID_QUERY_PARAM]: targetId }
      );
    }
  }
}
